import { customFieldText, indexCustomFields, normalizeCustomFieldName } from './custom-field-mapping'
import type { PublicCustomField } from './property-custom-fields'
import { createContentSanitizer, DROP_BODY_TAGS, type ContentHtmlPolicy } from './sanitize/content-html'
import { parseImageFieldValue } from './custom-field-image'

/**
 * Bedroom showcase photo/detail resolution (dashboard#1420 PR B, widget
 * parity for the toolkit's `bedroom-showcase` module — photo half only;
 * central#3211 explicitly deferred the `property_spaces` image column that
 * module's bed-data half would need).
 *
 * The platform's `/property-spaces` payload already carries a real numeric
 * `number` per bedroom (toolkit had to extract this from a free-text room
 * NAME as a fallback — `Data::resolve_indexes`, 'auto'/'name'/'order' —
 * because its ACF-synced spaces had no such field); this port skips that
 * ambiguity entirely and uses `number` directly, falling back to the
 * room's position among bedroom-type spaces (1-based) when `number` is
 * null.
 */

/** The custom-field name Central would carry for a bedroom's photo or blurb. */
export function bedroomFieldName(which: 'image' | 'details', index: number): string {
  return `bedroom${index}${which}`
}

// The photo-field parser now lives in `common/custom-field-image.ts`
// (dashboard#1574), so every image-typed custom field shares one parser and
// one test suite. These exports stay so nothing that imports them from here
// changes.
export {
  parseImageFieldValue as parseBedroomImages,
  IMAGE_FIELD_MAX_URLS as BEDROOM_IMAGES_MAX,
  IMAGE_FIELD_MAX_INPUT as BEDROOM_IMAGES_MAX_INPUT,
} from './custom-field-image'

// ── Details-field sanitizing ──────────────────────────────────────────
//
// The `bedroomNdetails` field is free-form pasted markup. Only basic
// structure survives (paragraphs, line breaks, bold/italic/underline, lists,
// links); every attribute, icon and wrapper element is removed. Rendered via
// dangerouslySetInnerHTML, so the sanitizer is the only thing standing
// between an operator's pasted HTML and the page.
//
// #1518: that sanitizer is the shared content sanitizer
// (common/sanitize/content-html.ts: js-xss behind guardContentTags, the
// module the content widget already renders through) under the policy
// below, which keeps every rule #1497's private tokenizer guaranteed. The
// tokenizer itself is gone; what stays in this file is presentation: the
// plain-text fast path, whitespace tightening, and the removal of structure
// that emptied out when its icon was stripped.

/**
 * Bound on the RAW input length, applied before anything else runs. A
 * `bedroomNdetails` blurb is a short human-written caption, not a document;
 * 20,000 characters is generous headroom over any real one while keeping a
 * pathological value cheap on the SSR request path (this runs inside
 * `resolveBedroomMedia` -> the widget's `mediaBySpace` memo -> `renderToString`,
 * once per bedroom). The policy carries the same number so the sanitizer
 * bounds itself even when called directly.
 */
export const MAX_DETAILS_LENGTH = 20_000

/**
 * What #1497's tokenizer guaranteed, expressed as content-html policy:
 *
 * - the toolkit port's allowlist (`u` in, headings out; the content widget's
 *   default has h2-h4 and no `u`);
 * - the port's drop list: `video`, `audio` and `canvas` lose their fallback
 *   text with the element, on top of the shared DROP_BODY_TAGS (the content
 *   widget keeps that text as copy);
 * - `href` only on `<a>` (no `title`), scheme-checked by safeContentHref and
 *   then held to the port's stricter rule: absolute http(s)/mailto/tel, a
 *   rooted same-origin path or a fragment, and no whitespace, quote or
 *   control byte anywhere in the value. Protocol-relative `//host` is
 *   refused too, which the port's rooted check let through (#1518);
 * - `rel="nofollow noopener noreferrer"` on every link: an externally
 *   supplied link on an SEO-indexed page never passes link equity and never
 *   reaches `window.opener`;
 * - the HTML tokenizer's reading of prose brackets and comments (a `<` that
 *   cannot start markup is text, a comment is one token consumed whole);
 * - balanced output: no tag reaches the page unclosed, a hrefless anchor is
 *   unwrapped to its text.
 */
export const BEDROOM_DETAILS_POLICY = {
  allowedTags: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a'],
  allowedAttrs: { a: ['href'] },
  dropBodyTags: [...DROP_BODY_TAGS, 'video', 'audio', 'canvas'],
  rel: 'nofollow noopener noreferrer',
  maxLength: MAX_DETAILS_LENGTH,
  relativeHrefs: 'rooted',
  tokenizer: 'html',
  balanceTags: true,
} as const satisfies ContentHtmlPolicy

const sanitizeDetailsHtml = createContentSanitizer(BEDROOM_DETAILS_POLICY)

const BLOCK_TAGS = 'ul|ol|li|p'

function plainText(text: string): string {
  return text
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]*[\r\n]+[ \t]*/g, '<br>')
    .trim()
}

/**
 * Reduce operator-pasted markup to a small safe-HTML subset. `''` when
 * nothing readable is left. The result is meant for `dangerouslySetInnerHTML`
 * — every attribute is stripped except a scheme-validated `href` on `<a>`,
 * every tag not on the policy allowlist is dropped (with its contents for
 * script/style/svg and the rest of DROP_BODY_TAGS), and no tag survives
 * that can carry `onerror=`/`javascript:`/etc.
 */
export function sanitizeBedroomDetails(html: string): string {
  const trimmed = html.trim().slice(0, MAX_DETAILS_LENGTH)
  if (trimmed === '') return ''
  // Plain text never goes through the HTML sanitizer: a `>` in prose stays a
  // `>` (text in HTML), and authored line breaks become <br>.
  if (!trimmed.includes('<')) return plainText(trimmed)

  let out = sanitizeDetailsHtml(trimmed)

  // Whitespace: the source was indented for a page builder. Safe on
  // sanitizer output: the only attribute is a href the policy already
  // refused if it contained whitespace, so this touches text and the gaps
  // between tags only.
  out = out.replace(/\s+/g, ' ')
  out = out.replace(new RegExp(`\\s*(<\\/?(?:${BLOCK_TAGS})>|<br>)\\s*`, 'gi'), '$1')

  // Empty structure left behind by removed icons (an <li> whose only
  // child was an <svg>). Repeat until stable — an emptied <li> can empty
  // its <ul>.
  let before: string
  do {
    before = out
    out = out.replace(new RegExp(`<(${BLOCK_TAGS}|strong|b|em|i|u)>\\s*<\\/\\1>`, 'gi'), '')
  } while (before !== out)

  return out.trim()
}


// ── Media resolution ──────────────────────────────────────────────────

export type BedroomMedia = {
  images: string[]
  details: string
}

/**
 * A room's photo(s) and blurb from the property's imported custom fields,
 * for the given bedroomN index. `{images: [], details: ''}` when neither
 * field was imported for this property — the caller renders nothing extra
 * in that case (field-gated, same principle as the generic custom-fields
 * block). dashboard#1574: the hook's `select` (usePropertyCustomFields,
 * common/property-custom-fields.ts) already normalized an image field to
 * the canonical newline-joined list, and the parser is idempotent on that
 * form, so parsing here again is a no-op for hook data and a full parse for
 * a caller holding a raw record (tests, any future direct consumer).
 */
export function resolveBedroomMedia(index: number, customFields: PublicCustomField[] | null | undefined): BedroomMedia {
  if (!customFields?.length) return { images: [], details: '' }

  const fieldIndex = indexCustomFields(customFields)
  const imageField = fieldIndex.get(normalizeCustomFieldName(bedroomFieldName('image', index)))
  const detailsField = fieldIndex.get(normalizeCustomFieldName(bedroomFieldName('details', index)))

  // Coerced for the same reason resolveCustomFields coerces: a non-string
  // `value` on this opaque payload otherwise reached `.trim()` inside
  // renderToString and 500'd the whole property route.
  return {
    images: imageField ? parseImageFieldValue(customFieldText(imageField.value)) : [],
    details: detailsField ? sanitizeBedroomDetails(customFieldText(detailsField.value)) : '',
  }
}
